// src/services/printer/mockPrinterService.js
import { multiPrinterService } from './printer.service';

class MockPrinterService {
  constructor() {
    this.isTestMode = import.meta.env.VITE_PRINTER_MODE === 'test';
    this.printerHandlers = new Map();
    this.encoder = new TextEncoder();
    
    // Inicializar una impresora por defecto en modo test
    if (this.isTestMode) {
      this.initializeDefaultPrinter();
    }
  }

  initializeDefaultPrinter() {
    const mockPrinterId = 'MOCK-DEFAULT-PRINTER';
    this.printerHandlers.set(mockPrinterId, {
      id: mockPrinterId,
      name: 'Impresora Simulada',
      config: {
        printKitchenOrders: true,
        printBarOrders: true,
        autoCustomerBills: true,
        isEnabled: true,
        isDefault: true
      },
      isConnected: true
    });

    this.savePrinterConfigurations();
  }

  savePrinterConfigurations() {
    try {
      const printers = Array.from(this.printerHandlers.entries()).map(
        ([id, handler]) => ({
          id,
          name: handler.name,
          config: handler.config,
        })
      );

      localStorage.setItem("connectedPrinters", JSON.stringify(printers));

      const configs = {};
      this.printerHandlers.forEach((handler, id) => {
        configs[id] = handler.config;
      });
      localStorage.setItem("printerConfigurations", JSON.stringify(configs));
    } catch (error) {
      console.error("Error saving printer configurations:", error);
    }
  }

  formatTicket(lines) {
    const width = 40;
    const separator = '─'.repeat(width);
    const doubleSeparator = '═'.repeat(width);

    return [
      '',
      `┌${doubleSeparator}┐`,
      ...lines.map(line => `│ ${line.padEnd(width - 2)} │`),
      `└${doubleSeparator}┘`,
      ''
    ].join('\n');
  }

  formatTitle(text) {
    const width = 36;
    const padding = Math.floor((width - text.length) / 2);
    return ' '.repeat(padding) + text;
  }

  formatAmount(amount) {
    return `$${Number(amount).toFixed(2).padStart(8)}`;
  }

  formatDateTime() {
    return new Date().toLocaleString();
  }

  log(message, data = '') {
    if (this.isTestMode) {
      console.log(`[MOCK PRINTER] ${message}`, data);
    }
  }

  async connectPrinter() {
    if (this.isTestMode) {
      const mockPrinterId = `MOCK-${Date.now()}`;
      const ticketContent = this.formatTicket([
        this.formatTitle('CONEXIÓN DE IMPRESORA'),
        ''.padEnd(38),
        `ID: ${mockPrinterId}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        this.formatTitle('CONEXIÓN EXITOSA')
      ]);
      
      console.log(ticketContent);
      
      this.printerHandlers.set(mockPrinterId, {
        id: mockPrinterId,
        name: 'Impresora Simulada',
        config: {
          printKitchenOrders: true,
          printBarOrders: true,
          autoCustomerBills: true,
          isEnabled: true
        }
      });

      this.savePrinterConfigurations();
      return mockPrinterId;
    }
    
    return await multiPrinterService.connectPrinter();
  }

  async testPrint(printerId) {
    if (this.isTestMode) {
      const ticketContent = this.formatTicket([
        this.formatTitle('PRUEBA DE IMPRESIÓN'),
        ''.padEnd(38),
        'Impresora configurada correctamente',
        `ID: ${printerId}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        this.formatTitle('PRUEBA EXITOSA')
      ]);
      
      console.log(ticketContent);
      return true;
    }
    
    return await multiPrinterService.testPrint(printerId);
  }

  async printOrder(orderData, destination, items) {
    if (this.isTestMode) {
      const lines = [
        this.formatTitle(destination),
        ''.padEnd(38),
        `Mesa: ${orderData.mesaId || orderData.descriptionTable}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        ...items.map(item => [
          `${item.quantity}x ${item.name}`,
          item.comment ? `  Nota: ${item.comment}` : null,
          item.selectedAddOn ? `  Opción: ${item.selectedAddOn}` : null
        ]).flat().filter(Boolean),
      ];

      const ticketContent = this.formatTicket(lines);
      console.log(ticketContent);
      return true;
    }

    return await multiPrinterService.printOrder(orderData, destination, items);
  }

  async printCustomerBill(order, printerId) {
    if (this.isTestMode) {
      let total = 0;
      const itemLines = order.items.map(item => {
        const itemTotal = item.price * item.quantity;
        total += itemTotal;
        return [
          `${item.quantity}x ${item.name}`,
          `  ${this.formatAmount(item.price)} c/u = ${this.formatAmount(itemTotal)}`
        ];
      }).flat();

      const ticketContent = this.formatTicket([
        this.formatTitle('TICKET DE PAGO'),
        ''.padEnd(38),
        `Ticket #${order._id || order.id}`,
        `Mesa: ${order.table || order.descriptionTable}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        ...itemLines,
        ''.padEnd(38),
        `TOTAL: ${this.formatAmount(total)}`,
        `Forma de pago: ${order.payment || 'No especificado'}`,
        ''.padEnd(38),
        this.formatTitle('¡Gracias por Elegirnos!')
      ]);

      console.log(ticketContent);
      return true;
    }

    return await multiPrinterService.printCustomerBill(order, printerId);
  }

  async printBarOrder(order, printerId) {
    if (this.isTestMode) {
      const lines = [
        this.formatTitle('COMANDA BAR'),
        ''.padEnd(38),
        `Mesa: ${order.mesaId}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        ...order.items.map(item => [
          `${item.quantity}x ${item.name}`,
          item.selectedAddon ? `  Opción: ${item.selectedAddon}` : null,
          item.comment ? `  Nota: ${item.comment}` : null
        ]).flat().filter(Boolean)
      ];
  
      const ticketContent = this.formatTicket(lines);
      console.log(ticketContent);
      return true;
    }
  
    return await multiPrinterService.printBarOrder(order, printerId);
  }

  async printCancellationTicket(orderId, cancelledProduct, printerId) {
    console.log("Imprimiendo ticket de cancelación:", {
      orderId,
      cancelledProduct,
      printerId
    });

    if (this.isTestMode) {
      const ticketContent = this.formatTicket([
        this.formatTitle('TICKET DE CANCELACIÓN'),
        ''.padEnd(38),
        `Ticket #${orderId}`,
        `Mesa: ${cancelledProduct.table}`,
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        'PRODUCTO CANCELADO:',
        cancelledProduct.name,
        cancelledProduct.quantity > 1 ? `Cantidad: ${cancelledProduct.quantity}` : '',
        ''.padEnd(38),
        this.formatTitle('CANCELACIÓN CONFIRMADA')
      ]);

      console.log(ticketContent);
      return true;
    }

    return await multiPrinterService.printCancellationTicket(orderId, cancelledProduct, printerId);
  }

  async printProductSummary(printerId, sales, metrics) {
    if (this.isTestMode) {
      const products = sales.transactions.reduce((acc, transaction) => {
        transaction.items.forEach((item) => {
          const existing = acc.find(p => p.name === item.name);
          if (existing) {
            existing.quantity += item.quantity;
            existing.total += item.price * item.quantity;
          } else {
            acc.push({
              name: item.name,
              quantity: item.quantity,
              price: item.price,
              total: item.price * item.quantity
            });
          }
        });
        return acc;
      }, []);

      const lines = [
        this.formatTitle('RESUMEN DE PRODUCTOS'),
        ''.padEnd(38),
        `Fecha: ${this.formatDateTime()}`,
        ''.padEnd(38),
        ...products.map(product => [
          product.name,
          `  ${product.quantity}x ${this.formatAmount(product.price)} = ${this.formatAmount(product.total)}`
        ]).flat(),
        ''.padEnd(38),
        `Total ventas: ${this.formatAmount(metrics.totalSales)}`,
      ];

      const ticketContent = this.formatTicket(lines);
      console.log(ticketContent);
      return true;
    }

    return await multiPrinterService.printProductSummary(printerId, sales, metrics);
  }

  getAllPrinters() {
    if (this.isTestMode) {
      // Si no hay impresoras, inicializar la por defecto
      if (this.printerHandlers.size === 0) {
        this.initializeDefaultPrinter();
      }
      
      const printers = Array.from(this.printerHandlers.values()).map(printer => ({
        ...printer,
        isConnected: true
      }));
      
      console.log('Impresoras disponibles:', printers);
      return printers;
    }

    return multiPrinterService.getAllPrinters();
  }

  updatePrinterConfig(printerId, config) {
    if (this.isTestMode) {
      const printer = this.printerHandlers.get(printerId);
      if (printer) {
        printer.config = { ...printer.config, ...config };
        this.printerHandlers.set(printerId, printer);
        this.log('Configuración de impresora actualizada', { printerId, config });
        this.savePrinterConfigurations();
        return true;
      }
      return false;
    }

    return multiPrinterService.updatePrinterConfig(printerId, config);
  }
}

let printerServiceInstance = null;

export const getPrinterService = () => {
  if (!printerServiceInstance) {
    if (import.meta.env.VITE_PRINTER_MODE === 'test') {
      printerServiceInstance = new MockPrinterService();
    } else {
      printerServiceInstance = multiPrinterService;
    }
  }
  return printerServiceInstance;
};